'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { FinderOpportunity, LocationGroup, MyServeResponse } from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import { fetchLocations, fetchMyResponses, fetchOpportunity } from '@/lib/api';
import { onMpTokenChange } from '@/lib/campus';
import { CampusSvgProvider } from './CampusIcon';
import { CollectionPage } from './CollectionPage';
import { OpportunityPage, OpportunitySkeleton } from './OpportunityPage';

interface ServeDetailsProps {
  apiBaseUrl: string;
  opportunityId: number | null;
  /** Where "All opportunities" goes (stock `returnurl`) */
  finderUrl?: string;
  /** Stock `showfulladdress` */
  showFullAddress?: boolean;
}

const DEFAULT_FINDER_URL = 'https://woodsidebible.org/serve/';

type Status = 'loading' | 'ready' | 'missing' | 'error';

/**
 * The details page: everything the finder's sheet shows, at page scale,
 * sign-up included.
 */
export function ServeDetails({
  apiBaseUrl,
  opportunityId,
  finderUrl,
  showFullAddress = false,
}: ServeDetailsProps) {
  const [opportunity, setOpportunity] = useState<FinderOpportunity | null>(null);
  const [links, setLinks] = useState<{ details: string; finder: string } | null>(null);
  const [status, setStatus] = useState<Status>(opportunityId ? 'loading' : 'missing');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [mine, setMine] = useState<MyServeResponse[]>([]);

  useEffect(() => {
    if (!opportunityId) return;
    const controller = new AbortController();
    const { signal } = controller;
    setStatus('loading');

    fetchOpportunity(apiBaseUrl, opportunityId, signal)
      .then((data) => {
        if (!data) {
          setStatus('missing');
          return;
        }
        setOpportunity(data.opportunity);
        setLinks(data.links);
        setStatus('ready');
      })
      .catch((err) => {
        if (signal.aborted) return;
        console.error('Serve details failed to load:', err);
        setStatus('error');
      });

    fetchLocations(apiBaseUrl, signal)
      .then(setLocations)
      .catch(() => {
        /* campus icons are decoration */
      });

    return () => controller.abort();
  }, [apiBaseUrl, opportunityId]);

  const refreshMine = useCallback(() => {
    if (!opportunityId) return;
    void fetchMyResponses(apiBaseUrl).then((r) =>
      setMine((r ?? []).filter((x) => x.opportunityId === opportunityId))
    );
  }, [apiBaseUrl, opportunityId]);
  useEffect(() => {
    refreshMine();
    return onMpTokenChange(() => refreshMine());
  }, [refreshMine]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const backUrl = finderUrl || links?.finder || DEFAULT_FINDER_URL;

  if (status === 'missing' || status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">
          {status === 'missing'
            ? "We couldn't find that opportunity."
            : "We couldn't load this opportunity right now."}
        </p>
        <p className="max-w-md text-sm text-gray-600">
          {status === 'missing'
            ? 'It may have filled up or wrapped up. There are plenty of other places to serve.'
            : 'Please try again in a moment.'}
        </p>
        <a
          href={backUrl}
          className="inline-flex items-center gap-1 rounded-full bg-[#299c8f] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
        >
          Browse all opportunities
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </a>
      </div>
    );
  }

  if (status === 'loading' || !opportunity) {
    return <OpportunitySkeleton finderUrl={backUrl} />;
  }

  // An id that turns out to be a Collection (an old link, or the finder's
  // card before it learned) shows the Collection's own page.
  if (opportunity.isCollection) {
    return (
      <CollectionPage apiBaseUrl={apiBaseUrl} reference={opportunity.id} finderUrl={backUrl} />
    );
  }

  return (
    <CampusSvgProvider value={svgMap}>
      <OpportunityPage
        apiBaseUrl={apiBaseUrl}
        opportunity={opportunity}
        responses={mine}
        onResponded={refreshMine}
        finderUrl={backUrl}
        showFullAddress={showFullAddress}
      />
    </CampusSvgProvider>
  );
}
